import React from 'react';
import { X, Pencil } from 'lucide-react';
import { Entity, EntityType, ENTITY_TYPE_LABELS } from '../../../types/annotation';

interface EntityListProps {
  entities: Entity[];
  onDelete: (entityId: string) => void;
  onEdit?: (entity: Entity) => void;
  disabled?: boolean;
}

const ENTITY_TYPE_COLORS: Record<EntityType, string> = {
  person: 'bg-blue-100 text-blue-700',
  organization: 'bg-purple-100 text-purple-700',
  post_designation: 'bg-indigo-100 text-indigo-700',
  government_institution: 'bg-violet-100 text-violet-700',
  policy: 'bg-orange-100 text-orange-700',
  issue: 'bg-red-100 text-red-700',
  location: 'bg-green-100 text-green-700',
  programme_scheme: 'bg-teal-100 text-teal-700',
  thing_object: 'bg-gray-100 text-gray-700',
};

const SENTIMENT_ICONS: Record<string, { icon: string; color: string }> = {
  positive: { icon: '+', color: 'text-green-600' },
  negative: { icon: '−', color: 'text-red-600' },
  neutral: { icon: '○', color: 'text-gray-500' },
  mixed: { icon: '~', color: 'text-amber-600' },
};

const EntityList: React.FC<EntityListProps> = ({ entities, onDelete, onEdit, disabled = false }) => {
  if (entities.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic">No entities tagged yet.</p>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {entities.map((entity) => {
        const typeColor = ENTITY_TYPE_COLORS[entity.entityType] ?? 'bg-gray-100 text-gray-700';
        const sentimentInfo = entity.sentiment ? SENTIMENT_ICONS[entity.sentiment] : null;

        return (
          <div
            key={entity.entityId}
            className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-full text-xs shadow-sm max-w-full"
          >
            {/* Type badge */}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide shrink-0 ${typeColor}`}>
              {ENTITY_TYPE_LABELS[entity.entityType]}
            </span>

            {/* Name */}
            <span className="text-gray-800 font-medium truncate max-w-[120px]" title={entity.entityName}>
              {entity.entityName}
            </span>

            {/* Sentiment indicator */}
            {sentimentInfo && (
              <span className={`font-bold shrink-0 ${sentimentInfo.color}`} title={entity.sentiment ?? ''}>
                {sentimentInfo.icon}
              </span>
            )}

            {/* Edit button */}
            {onEdit && !disabled && (
              <button
                onClick={() => onEdit(entity)}
                className="p-0.5 text-gray-400 hover:text-violet-600 transition-colors shrink-0"
                aria-label="Edit entity"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}

            {/* Delete button */}
            {!disabled && (
              <button
                onClick={() => onDelete(entity.entityId)}
                className="p-0.5 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                aria-label="Remove entity"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EntityList;
